import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../config';

const EmailVerification = ({ onVerificationComplete }) => {
  const [verificationStatus, setVerificationStatus] = useState('pending'); // 'pending', 'success', 'error', 'expired'
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [token, setToken] = useState('');
  const [verifiedUser, setVerifiedUser] = useState(null);

  useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl && tokenFromUrl.trim() !== '') {
      setToken(tokenFromUrl);
      verifyEmail(tokenFromUrl);
    } else {
      // No token provided, show error state
      setVerificationStatus('expired');
      setError('No verification token provided. Please check your email for the verification link.');
    }
  }, []);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await axios.post(getApiUrl('/verify-email'), {
        token: verificationToken
      }, {
        withCredentials: true
      });

      setVerificationStatus('success');
      setVerifiedUser(response.data.user);
      if (onVerificationComplete) {
        onVerificationComplete(response.data.user);
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setVerificationStatus('expired');
        setError('This verification link has expired or is invalid.');
      } else {
        setVerificationStatus('error');
        setError(err.response?.data?.error || 'Failed to verify email. Please try again.');
      }
    }
  };

  const resendVerification = async () => {
    setIsResending(true);
    setError('');

    try {
      await axios.post(getApiUrl('/resend-verification'), {}, {
        withCredentials: true
      });
      
      setVerificationStatus('pending');
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setIsResending(false);
    }
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'success':
        return (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Email Verified!</h2>
            <p className="text-gray-300 mb-6">
              Your email has been successfully verified. You can now use all features of Enable.AI.
            </p>
            <button
              onClick={() => {
                // Clean up URL and navigate to home
                window.history.replaceState({}, document.title, '/');
                // This will trigger the parent component to show home
                if (onVerificationComplete && verifiedUser) {
                  onVerificationComplete(verifiedUser);
                }
              }}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        );

      case 'error':
      case 'expired':
        return (
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-gray-300 mb-4">{error}</p>
            <div className="space-y-3">
              <button
                onClick={resendVerification}
                disabled={isResending}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto transition-colors"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  // Clean up URL and navigate to login
                  window.history.replaceState({}, document.title, '/');
                  // This will trigger the parent component to show login
                  if (onVerificationComplete) {
                    onVerificationComplete(null);
                  }
                }}
                className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-400 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-white mb-2">Verifying Email...</h2>
            <p className="text-gray-300">Please wait while we verify your email address.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-900/50 backdrop-blur-sm rounded-lg shadow-lg p-8 border border-gray-800">
        {renderContent()}
      </div>
    </div>
  );
};

export default EmailVerification; 